import { FunctionComponent, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { CreateKidDocumentValues } from '../CreateKidDocumentModal';
import TokenService from '../../../../../../services/token-service';
import { useGetLatestKidFigures } from '../../../../../../api/kids/hooks/queries';
import DocumentPreview from './ReviewDocumentPreview';
import KidFiguresOverview from '../KidFiguresOverview';
import { pickLatestKidFigure } from '../../utils/kidFigureUtils';
import { countryCodeToName } from '../../../../../../utilities/generalMappings';

const StepReview: FunctionComponent<{
  shareClassISIN?: string;
  shareClassId: string;
  shareClassName: string;
}> = ({ shareClassISIN, shareClassId, shareClassName }) => {
  const { watch } = useFormContext<CreateKidDocumentValues>();
  const documentSourceType = watch('documentSourceType');
  const creationMethod = watch('creationMethod');
  const v = watch();
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const { user_client_id: clientId } = TokenService.getUser()!;

  const {
    data: latestKidFigures,
    isLoading: isLoadingKidFigures,
    isError: isErrorKidFigures,
    error: kidFiguresError,
  } = useGetLatestKidFigures(clientId, v.fundId, shareClassId);


  const kidFigures = pickLatestKidFigure(latestKidFigures);


  // PDF preview generation is now handled entirely inside ReviewDocumentPreview.
  const selectedTemplates = Object.entries(v.templates)
    .filter(([, val]) => val)
    .map(([key]) => key);
  const templateLabelMap: Record<
    string,
    { label: string; type: 'Manco' | 'Subfund' }
  > = {
    globalRiskDisclosure: { label: 'Global Risk Disclosure', type: 'Manco' },
    companyInformationBlock: {
      label: 'Company Information Block',
      type: 'Manco',
    },
    performanceDisclaimer: { label: 'Performance Disclaimer', type: 'Manco' },
    investmentObjectiveStatement: {
      label: 'Investment Objective Statement',
      type: 'Subfund',
    },
  };

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col space-y-2">
      <div className="grid min-h-0 flex-1 grid-cols-2 gap-4 overflow-hidden">
        <div className="scrollbar-custom min-h-0 space-y-4 overflow-y-auto pr-1">
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-600">
              <span className="text-gray-500">Language:</span>
              <span className="font-medium text-gray-800">{v.language || '—'}</span>
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-600">
              <span className="text-gray-500">Jurisdiction:</span>
              <span className="font-medium text-gray-800">
                {v.jurisdiction ? countryCodeToName(v.jurisdiction) : '—'}
              </span>
            </span>
          </div>


          <div className="border-b border-gray-200 pb-1">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Step 1 - Costs and Figures
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <h4 className="mb-2 text-sm font-semibold text-gray-800">
                Costs
              </h4>
              <ul className="grid grid-cols-1 gap-2 text-sm text-gray-700 sm:grid-cols-2">
                <li className="rounded-md border border-gray-200 bg-gray-50 p-2">
                  <div className="text-xs text-gray-500">Transaction Cost</div>
                  <div className="text-sm font-medium text-gray-800">
                    {v.transactionCost ? `${v.transactionCost}%` : '—'}
                  </div>
                </li>
                <li className="rounded-md border border-gray-200 bg-gray-50 p-2">
                  <div className="text-xs text-gray-500">Ongoing Charges</div>
                  <div className="text-sm font-medium text-gray-800">
                    {v.ongoingCharges ? `${v.ongoingCharges}%` : '—'}
                  </div>
                </li>
                <li className="rounded-md border border-gray-200 bg-gray-50 p-2">
                  <div className="text-xs text-gray-500">Entry Fee</div>
                  <div className="text-sm font-medium text-gray-800">
                    {v.entryFee ? `${v.entryFee}%` : '—'}
                  </div>
                </li>
                <li className="rounded-md border border-gray-200 bg-gray-50 p-2">
                  <div className="text-xs text-gray-500">Exit Fee</div>
                  <div className="text-sm font-medium text-gray-800">
                    {v.exitFee ? `${v.exitFee}%` : '—'}
                  </div>
                </li>
                {v.has_performance_fee && (
                  <>
                    <li className="rounded-md border border-gray-200 bg-gray-50 p-2">
                      <div className="text-xs text-gray-500">
                        Applied Performance Fee
                      </div>
                      <div className="text-sm font-medium text-gray-800">
                        {v.appliedPerformanceFee
                          ? `${v.appliedPerformanceFee}%`
                          : '—'}
                      </div>
                    </li>
                    <li className="rounded-md border border-gray-200 bg-gray-50 p-2">
                      <div className="text-xs text-gray-500">
                        Notional Performance Fee
                      </div>
                      <div className="text-sm font-medium text-gray-800">
                        {v.notionalPerformanceFee
                          ? `${v.notionalPerformanceFee}%`
                          : '—'}
                      </div>
                    </li>
                  </>
                )}
                <li className="rounded-md border border-gray-200 bg-gray-50 p-2 sm:col-span-2">
                  <div className="text-xs text-gray-500">Has Performance Fee</div>
                  <div className="mt-0.5">
                    <span
                      className={
                        'inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ' +
                        (v.has_performance_fee
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-200 text-gray-700')
                      }
                    >
                      {v.has_performance_fee ? 'Yes' : 'No'}
                    </span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-800">
                  Figures Calculation
                </h4>
              </div>
              {isLoadingKidFigures ? (
                <div className="text-sm text-gray-500">Fetching figures…</div>
              ) : isErrorKidFigures ? (
                <div className="text-sm text-red-700">
                  Failed to load figures
                  {kidFiguresError ? (
                    <div className="mt-1 text-xs text-red-600">
                      {String(kidFiguresError)}
                    </div>
                  ) : null}
                </div>
              ) : kidFigures ? (
                <div className="overflow-x-auto">
                  <KidFiguresOverview
                    figuresRecord={kidFigures}
                    clientId={clientId}
                    fundId={v.fundId}
                    shareClassId={shareClassId}
                  />
                </div>
              ) : (
                <div className="text-sm text-gray-500">No figures.</div>
              )}
            </div>
          </div>

          {/* Document Setup - Top Section */}
          <div className="border-b border-gray-200 pb-1">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Step 2 - Document Layout
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <h4 className="mb-3 text-sm font-semibold text-gray-800">
              Document Setup
            </h4>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div className="md:col-span-2 lg:col-span-3">
                <div className="mt-1 space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                  {!v.documentSourceType && (
                    <p className="text-sm text-gray-500">
                      No document layout option selected.
                    </p>
                  )}

                  {v.documentSourceType === 'new' && (
                    <div className="space-y-2">
                      <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">
                        Make New Document
                      </span>
                      <div className="flex flex-wrap gap-2">
                        <span
                          className={
                            'rounded-md border px-2 py-1 text-xs ' +
                            (v.creationMethod === 'blank'
                              ? 'border-mainblue bg-blue-50 text-mainblue'
                              : 'border-gray-300 bg-white text-gray-500')
                          }
                        >
                          Blank document
                        </span>
                        <span
                          className={
                            'rounded-md border px-2 py-1 text-xs ' +
                            (v.creationMethod === 'recommended'
                              ? 'border-mainblue bg-blue-50 text-mainblue'
                              : 'border-gray-300 bg-white text-gray-500')
                          }
                        >
                          Recommended layout
                        </span>
                      </div>
                      {v.creationMethod === 'recommended' && (
                        <div className="rounded-md border border-mainblue/30 bg-white p-2">
                          <div className="text-[11px] uppercase tracking-wide text-gray-500">
                            Selected Layout Style
                          </div>
                          <div className="text-sm font-medium text-gray-800">
                            {v.layoutStyleName || v.layoutStyle || 'None selected'}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {v.documentSourceType === 'duplicate' && (
                    <div className="space-y-2">
                      <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">
                        Duplicate Existing Document
                      </span>
                      <div className="space-y-2 rounded-md border border-gray-300 bg-white p-2 text-sm text-gray-700">
                        <div>
                          <div className="text-xs text-gray-500">Fund</div>
                          <div className="font-medium text-gray-800">
                            {v.template_fund_name || 'No fund selected'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Share Class</div>
                          <div className="font-medium text-gray-800">
                            {v.template_share_class_name ||
                              v.template_share_class_id ||
                              'No share class selected'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Templates/Content Section */}
          <div className="border-b border-gray-200 pb-1">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Step 3 - Templates
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <h4 className="mb-2 text-sm font-semibold text-gray-800">
                Templates/Content
              </h4>
              {selectedTemplates.length === 0 ? (
                <div className="text-sm text-gray-500">
                  No templates selected
                </div>
              ) : (
                <ul className="space-y-1 text-sm text-gray-700">
                  {selectedTemplates.map((k) => (
                    <li key={k} className="flex items-center justify-between">
                      <button
                        type="button"
                        className="text-left font-medium text-mainblue hover:underline"
                        onClick={() => setPreviewKey(k)}
                      >
                        {templateLabelMap[k]?.label ?? k}
                      </button>
                      <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                        {templateLabelMap[k]?.type ?? 'Template'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <h4 className="mb-2 text-sm font-semibold text-gray-800">
                Template Details
              </h4>
              {(() => {
                const templateLabelMapInner: Record<
                  string,
                  { label: string; type: 'Manco' | 'Subfund' }
                > = {
                  globalRiskDisclosure: {
                    label: 'Global Risk Disclosure',
                    type: 'Manco',
                  },
                  companyInformationBlock: {
                    label: 'Company Information Block',
                    type: 'Manco',
                  },
                  performanceDisclaimer: {
                    label: 'Performance Disclaimer',
                    type: 'Manco',
                  },
                  investmentObjectiveStatement: {
                    label: 'Investment Objective Statement',
                    type: 'Subfund',
                  },
                };
                const selectedKeys = Object.entries(v.templates)
                  .filter(([, val]) => val)
                  .map(([k]) => k);
                const currentKey = previewKey || selectedKeys[0];
                if (!currentKey) {
                  return (
                    <div className="text-sm text-gray-500">
                      No template selected
                    </div>
                  );
                }
                const meta = templateLabelMapInner[currentKey] || {
                  label: currentKey,
                  type: 'Manco' as const,
                };
                const text =
                  currentKey === 'globalRiskDisclosure'
                    ? 'Standard risk warnings and disclaimers that apply to investment products.'
                    : currentKey === 'companyInformationBlock'
                      ? 'Management company details, registration info, and contact details.'
                      : currentKey === 'performanceDisclaimer'
                        ? 'Past performance does not predict future returns. Forward-looking statements apply.'
                        : 'Investment objectives, strategy, and target outcomes for the fund.';

                return (
                  <div className="space-y-2 text-sm text-gray-700">
                    <div>
                      Name: <span className="font-medium">{meta.label}</span>
                    </div>
                    <div>
                      Type:{' '}
                      <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                        {meta.type}
                      </span>
                    </div>
                    <div>
                      <div className="mb-1 text-xs font-semibold text-gray-700">
                        Template Text
                      </div>
                      <div className="rounded border border-gray-200 bg-white p-2 text-xs">
                        {text}
                      </div>
                    </div>
                    <div>
                      <div className="mb-1 text-xs font-semibold text-gray-700">
                        Template Associations
                      </div>
                      <ul className="list-disc pl-5 text-xs text-gray-700">
                        <li>European Growth Fund — EUR ACC (EU001234567)</li>
                        <li>European Value Fund — EUR DIS (EU001234568)</li>
                      </ul>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Right column: Document Preview */}
        <div className="flex min-h-0 flex-col">
          <DocumentPreview
            documentSourceType={documentSourceType}
            creationMethod={creationMethod}
            shareClassId={shareClassId}
            shareClassName={shareClassName}
          />
        </div>
      </div>
    </div>
  );
};

export default StepReview;
